import {
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Spin,
} from "antd";
import { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";

import { useCreateEditTeam } from "../hooks/useCreateEditTeam";
import { getTeamUsers } from "../../../actionCreator/teamManagementActionCreator";
import { searchUsers } from "../../../actionCreator/userManagementActionCreator";
import { getUserDisplayName } from "../../../utils/teamAccess";
const { TextArea } = Input;
const { Option } = Select;

const CreateTeamForm = ({ initialValues, closeModal }) => {
  const dispatch = useDispatch();
  const [isDisable, setDisable] = useState(false);
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
  const [form] = Form.useForm();

  const teamUsers = useSelector(
    (state) => state.teamManagementReducer.teamUsers,
  );

  const {
    handleCreateUpdateTeam,
    error,
    loading,
    setError,
    handleRemoveUserFromTeam,
  } = useCreateEditTeam();

  useEffect(() => {
    if (initialValues?.id) {
      dispatch(getTeamUsers({ id: initialValues?.id }));
    }
  }, [initialValues, form, dispatch]);

  useEffect(() => {
    if (!initialValues?.id) return;

    form.setFieldsValue({
      ...initialValues,
      managerEmails:
        initialValues?.managerEmails ||
        (initialValues?.managerEmail
          ? initialValues.managerEmail
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : []),
      memberUserIds:
        initialValues?.memberUserIds ||
        teamUsers?.userTeams?.map((teamUser) => teamUser?.userId) ||
        [],
      allowManagersToViewEachOtherData:
        !!initialValues?.allowManagersToViewEachOtherData,
    });
  }, [form, initialValues, teamUsers?.userTeams]);

  useEffect(() => {
    const fetchDefault = async () => {
      try {
        const res = await dispatch(searchUsers());
        setUsers(res?.content || []);
      } catch (error) {}
    };

    fetchDefault();
  }, [dispatch]);

  const fetchOptions = debounce(async (value) => {
    setFetching(true);
    try {
      const res = await dispatch(searchUsers(value));
      setUsers(res?.content || []);
    } catch (err) {}
    setFetching(false);
  }, 500);

  const memberUserIds = Form.useWatch("memberUserIds", form) || [];
  const managerEmails = Form.useWatch("managerEmails", form) || [];

  const selectedManagerIds = users
    .filter((user) => managerEmails.includes(user?.email))
    .map((user) => user?.id)
    .filter(Boolean);

  const handleAddUser = () => {
    if (!selectedUsersToAdd?.length) return;

    const validUserIds = selectedUsersToAdd.filter(
      (userId) => !selectedManagerIds.includes(userId),
    );

    if (!validUserIds.length) {
      setSelectedUsersToAdd([]);
      return;
    }

    const nextIds = Array.from(
      new Set([...(memberUserIds || []), ...validUserIds]),
    );
    form.setFieldsValue({ memberUserIds: nextIds });
    setSelectedUsersToAdd([]);
  };

  const handleRemovePendingUser = (userId) => {
    form.setFieldsValue({
      memberUserIds: (memberUserIds || []).filter((id) => id !== userId),
    });
  };

  const currentTeamUserIds =
    teamUsers?.userTeams?.map((teamUser) => teamUser?.userId).filter(Boolean) ||
    [];

  const pendingUsers = (memberUserIds || [])
    .filter((userId) => !currentTeamUserIds.includes(userId))
    .filter((userId) => !selectedManagerIds.includes(userId))
    .map((userId) => users.find((user) => user?.id === userId))
    .filter(Boolean);

  const addableUsers = users.filter(
    (user) =>
      !(memberUserIds || []).includes(user?.id) &&
      !currentTeamUserIds.includes(user?.id) &&
      !selectedManagerIds.includes(user?.id),
  );

  useEffect(() => {
    if (!selectedManagerIds.length) return;

    const filteredMemberIds = (memberUserIds || []).filter(
      (userId) => !selectedManagerIds.includes(userId),
    );

    if (filteredMemberIds.length !== memberUserIds.length) {
      form.setFieldsValue({ memberUserIds: filteredMemberIds });
    }
  }, [form, memberUserIds, selectedManagerIds]);

  return (
    <>
      <div className="tw-bg-secondary-color tw-text-text-primary tw-text-lg tw-font-semibold tw-py-4 tw-px-6 tw-rounded-t-2xl">
        {initialValues?.id ? "Edit Team" : "Create Team"}
      </div>

      <Form
        layout="vertical"
        form={form}
        onFinish={(val) =>
          handleCreateUpdateTeam(
            {
              ...val,
              managerEmail: val?.managerEmails?.join(", ") || null,
              managerEmails: val?.managerEmails || [],
              memberUserIds: val?.memberUserIds || [],
            },
            initialValues,
            () => {
              form.resetFields();
              closeModal && closeModal();
            },
          )
        }
        onValuesChange={(changedValues, allValues) => {
          setError();
          if (initialValues?.id) {
            const normalizeValue = (key, value) => {
              if (["managerEmails", "memberUserIds"].includes(key)) {
                return JSON.stringify((value || []).slice().sort());
              }
              if (key === "allowManagersToViewEachOtherData") {
                return String(!!value);
              }
              return String(value || "").trim();
            };

            const initialFormValues = {
              teamName: initialValues?.teamName,
              description: initialValues?.description,
              location: initialValues?.location,
              managerEmails:
                initialValues?.managerEmails ||
                (initialValues?.managerEmail
                  ? initialValues.managerEmail
                      .split(",")
                      .map((e) => e.trim())
                      .filter(Boolean)
                  : []),
              memberUserIds:
                initialValues?.memberUserIds ||
                teamUsers?.userTeams?.map((teamUser) => teamUser?.userId) ||
                [],
              allowManagersToViewEachOtherData:
                !!initialValues?.allowManagersToViewEachOtherData,
            };

            const keysToTrack = [
              "teamName",
              "description",
              "location",
              "managerEmails",
              "memberUserIds",
              "allowManagersToViewEachOtherData",
            ];

            const hasChanges = keysToTrack.some(
              (key) =>
                normalizeValue(key, initialFormValues[key]) !==
                normalizeValue(key, allValues[key]),
            );
            setDisable(!hasChanges);
          }
        }}
        autoComplete="off"
      >
        <Card className="tw-h-[calc(100vh-350px)] tw-overflow-y-auto">
          {error && <p className="text-error text-center pb-2">{error}</p>}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<div className="fw-medium">Team Name</div>}
                name="teamName"
                rules={[{ required: true, message: "Please enter team name" }]}
              >
                <Input placeholder="Enter team name" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={<div className="fw-medium">Description</div>}
                name="description"
              >
                <TextArea rows={3} placeholder="Enter team description" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<div className="fw-medium">Location</div>}
                name="location"
              >
                <Input placeholder="Enter location" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<div className="fw-medium">Managers</div>}
                name="managerEmails"
                rules={[
                  {
                    required: true,
                    message: "Please select at least one manager",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Search users..."
                  onSearch={fetchOptions}
                  filterOption={false}
                  notFoundContent={fetching ? <Spin size="small" /> : null}
                  allowClear
                >
                  {users.map((user) => (
                    <Option key={user.id} value={user.email}>
                      {getUserDisplayName(user)} ({user?.email})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<div className="fw-medium">Manager Visibility</div>}
                name="allowManagersToViewEachOtherData"
                valuePropName="checked"
                className="!tw-mb-4"
              >
                <Checkbox>
                  Allow managers to view each other&apos;s data
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={24}>
              <div className="fw-medium">Add Team Users</div>
              <Form.Item name="memberUserIds" hidden>
                <Input />
              </Form.Item>
              <div className="tw-flex tw-gap-2 tw-items-start tw-mt-2">
                <Select
                  mode="multiple"
                  showSearch
                  value={selectedUsersToAdd}
                  placeholder="Search users to add..."
                  onSearch={fetchOptions}
                  onChange={setSelectedUsersToAdd}
                  filterOption={false}
                  notFoundContent={fetching ? <Spin size="small" /> : null}
                  allowClear
                  className="tw-flex-1"
                  optionLabelProp="label"
                  maxTagTextLength={18}
                >
                  {addableUsers.map((user) => (
                    <Option
                      key={user?.id}
                      value={user?.id}
                      label={getUserDisplayName(user)}
                    >
                      {getUserDisplayName(user)} ({user?.email})
                    </Option>
                  ))}
                </Select>
                <Button
                  type="default"
                  onClick={handleAddUser}
                  disabled={!selectedUsersToAdd?.length}
                  className="tw-h-[32px]"
                >
                  <PlusOutlined /> Add
                </Button>
              </div>
              {!!pendingUsers.length && (
                <div className="tw-mt-4">
                  <div className="fw-medium tw-mb-2">Users To Be Added</div>
                  {pendingUsers.map((user) => (
                    <Row
                      className="justify-between mt10 mb10"
                      key={`pending-${user?.id}`}
                    >
                      <Col span={22}>
                        {getUserDisplayName(user)} ({user?.email})
                      </Col>
                      <Col>
                        <CloseOutlined
                          className="text-error"
                          onClick={() => handleRemovePendingUser(user?.id)}
                        />
                      </Col>
                    </Row>
                  ))}
                </div>
              )}
            </Col>
            {!!initialValues?.id && (
              <Col span={24} className="tw-mt-4 mb20">
                <div className="fw-medium">Team Users</div>
                {teamUsers?.userTeams?.length ? (
                  <>
                    {teamUsers?.userTeams?.map((d) => (
                      <Row
                        className="justify-between mt10 mb10"
                        key={d?.id || d?.userId}
                      >
                        <Col span={22}>
                          {d?.userName || getUserDisplayName(d)} (
                          {d?.userEmail || d?.email})
                        </Col>
                        <Col>
                          <Popconfirm
                            title="Remove user"
                            description={`Are you sure to remove ${d?.userName} from team ${initialValues?.teamName}?`}
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() =>
                              handleRemoveUserFromTeam(d, () =>
                                dispatch(
                                  getTeamUsers({ id: initialValues?.id }),
                                ),
                              )
                            }
                          >
                            <CloseOutlined className="text-error" />
                          </Popconfirm>
                        </Col>
                      </Row>
                    ))}
                  </>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No users added to this team"
                  />
                )}
              </Col>
            )}
          </Row>
        </Card>
        <div className="d-flex tw-bg-secondary-color tw-rounded-b-2xl tw-py-4 tw-px-6 align-items-center justify-content-end gap-20">
          <Button
            className="tw-border-sub-text hover:!tw-border-sub-text tw-text-sm tw-text-sub-text hover:!tw-text-sub-text"
            disabled={loading}
            onClick={closeModal}
          >
            Cancel
          </Button>

          <Button
            disabled={loading || isDisable}
            type="primary"
            className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
            htmlType="submit"
          >
            {initialValues?.id ? "Save Changes" : "Create team"}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default CreateTeamForm;
